import React, { useState, useEffect, useMemo } from 'react';
import {
  Container, Header, Table, StatusIndicator, Box, SpaceBetween, Spinner,
  SegmentedControl, DateRangePicker
} from '@cloudscape-design/components';
import { apiFetch } from '../lib/apiFetch.js';
import DailyRunsChart from '../components/DailyRunsChart.jsx';

const API_BASE_URL = 'https://css2g9ujye.execute-api.ap-south-1.amazonaws.com';

const STATUS_MAP = {
  'active': { type: 'success', label: 'Active' },
  'in-dev': { type: 'in-progress', label: 'In Dev' },
  'coming-soon': { type: 'stopped', label: 'Coming Soon' },
  'outdated': { type: 'warning', label: 'Outdated' },
};

// Static entries for tools without S3 tracking (always at bottom)
const STATIC_TOOLS = [
  { tool: 'ASSR Weekly Insight', parent: 'Insights', users: 0, runs: 0, lastUsed: '-', status: 'outdated', topUsers: [], userBreakdown: [], itemsProcessed: 0, itemLabel: '' },
  { tool: 'DS Weekly Insight', parent: 'Insights', users: 0, runs: 0, lastUsed: '-', status: 'outdated', topUsers: [], userBreakdown: [], itemsProcessed: 0, itemLabel: '' },
  { tool: 'PLS Weekly Insight', parent: 'Insights', users: 0, runs: 0, lastUsed: '-', status: 'outdated', topUsers: [], userBreakdown: [], itemsProcessed: 0, itemLabel: '' },
  { tool: 'Smart AI Assistant', parent: 'AI', users: 0, runs: 0, lastUsed: '-', status: 'coming-soon', topUsers: [], userBreakdown: [], itemsProcessed: 0, itemLabel: '' },
];

// Desired group ordering
const GROUP_ORDER = ['Image Tools', 'Listing Creation', 'Sales Enablement', 'Insights', 'AI'];

// Within "Listing Creation" group, enforce this sub-order
const LISTING_CREATION_ORDER = ['From IC File', 'From Amazon (ASIN)', 'Listing Optimizer'];

// User filter options
const USER_FILTER_OPTIONS = [
  { id: 'all', text: 'All' },
  { id: 'exclude_test', text: 'Excl. Test' },
  { id: 'external', text: 'Non-Amazon' },
];

// Date range presets — type:"relative" is required by Cloudscape.
const DATE_RANGE_PRESETS = [
  { key: 'previous-7-days', amount: 7, unit: 'day', type: 'relative' },
  { key: 'previous-30-days', amount: 30, unit: 'day', type: 'relative' },
  { key: 'previous-90-days', amount: 90, unit: 'day', type: 'relative' },
  { key: 'previous-180-days', amount: 180, unit: 'day', type: 'relative' },
];

function formatDateParam(date) {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}${m}${day}`;
}

function resolveDateRange(value) {
  if (!value || value.type === 'absolute') {
    if (!value) return { startDate: '', endDate: '' };
    return {
      startDate: value.startDate ? formatDateParam(value.startDate) : '',
      endDate: value.endDate ? formatDateParam(value.endDate) : '',
    };
  }
  const now = new Date();
  const end = new Date(now);
  const start = new Date(now);
  if (value.unit === 'day') start.setDate(start.getDate() - value.amount);
  else if (value.unit === 'month') start.setMonth(start.getMonth() - value.amount);
  return { startDate: formatDateParam(start), endDate: formatDateParam(end) };
}

function applyUserFilter(tool, filter) {
  if (filter === 'all' || !tool.userBreakdown || tool.userBreakdown.length === 0) {
    return { users: tool.users, runs: tool.runs, topUsers: tool.topUsers, itemsProcessed: tool.itemsProcessed };
  }

  const filtered = tool.userBreakdown.filter((u) => {
    if (filter === 'exclude_test') return !u.isTest;
    if (filter === 'external') return !u.isAmazon;
    return true;
  });

  const runs = filtered.reduce((sum, u) => sum + u.count, 0);
  const users = filtered.length;
  const itemsProcessed = filtered.reduce((sum, u) => sum + (u.items || 0), 0);
  const topUsers = filtered
    .slice()
    .sort((a, b) => b.count - a.count)
    .slice(0, 3)
    .map(({ name, count, items }) => ({ name, count, items: items || 0 }));

  return { users, runs, topUsers, itemsProcessed };
}

const COLUMN_DEFS = [
  {
    id: 'tool', header: 'Tool', width: 200,
    cell: (item) => {
      if (item._isGroupHeader) {
        return <Box fontWeight="bold" fontSize="body-m">{item._groupName}</Box>;
      }
      return <Box margin={{ left: 'l' }}>{item.tool}</Box>;
    }
  },
  {
    id: 'users', header: 'Users', width: 70,
    cell: (item) => item._isGroupHeader ? '' : (item._filteredUsers || '-')
  },
  {
    id: 'runs', header: 'Total Runs', width: 90,
    cell: (item) => item._isGroupHeader ? '' : (item._filteredRuns || '-')
  },
  {
    id: 'itemsProcessed', header: 'Items Processed', width: 140,
    cell: (item) => {
      if (item._isGroupHeader) return '';
      if (!item.itemsProcessed || item.itemsProcessed === 0) return '-';
      const label = item.itemLabel || 'items';
      return (
        <span>
          <span style={{ fontWeight: 500 }}>{item.itemsProcessed.toLocaleString()}</span>
          <span style={{ color: 'var(--color-text-body-secondary, #687078)', fontSize: '12px', marginLeft: '4px' }}>{label}</span>
        </span>
      );
    }
  },
  {
    id: 'lastUsed', header: 'Last Used', width: 120,
    cell: (item) => item._isGroupHeader ? '' : item.lastUsed
  },
  {
    id: 'topUsers', header: 'Top Users', width: 300,
    cell: (item) => {
      if (item._isGroupHeader) return '';
      const topUsers = item._filteredTopUsers;
      if (!topUsers?.length) return '-';
      const bgs = ['#15803d', '#22c55e', '#86efac'];
      const fgs = ['#fff', '#fff', '#14532d'];
      return (
        <span style={{ display: 'flex', gap: '4px', flexWrap: 'nowrap' }}>
          {topUsers.map((u, i) => {
            const name = typeof u === 'string' ? u : u.name;
            const count = typeof u === 'string' ? null : u.count;
            const items = typeof u === 'string' ? null : u.items;
            let tooltipText = null;
            if (count != null) {
              tooltipText = `${count} run${count === 1 ? '' : 's'}`;
              if (items) tooltipText += ` · ${items.toLocaleString()} ${item.itemLabel || 'items'}`;
            }
            return (
              <span
                key={name}
                className={tooltipText ? 'tu-pill-trigger' : ''}
                onMouseEnter={tooltipText ? (e) => {
                  const tip = e.currentTarget.querySelector('.tu-pill-tip');
                  if (tip) {
                    const rect = e.currentTarget.getBoundingClientRect();
                    tip.style.top = `${rect.top - 4}px`;
                    tip.style.left = `${rect.left + rect.width / 2}px`;
                  }
                } : undefined}
              >
                <span style={{
                  background: bgs[i] || bgs[2], color: fgs[i] || fgs[2],
                  padding: '2px 8px', borderRadius: '10px', fontSize: '12px',
                  display: 'inline-block', whiteSpace: 'nowrap',
                }}>{name}</span>
                {tooltipText && (
                  <span className="tu-pill-tip" role="tooltip">{tooltipText}</span>
                )}
              </span>
            );
          })}
        </span>
      );
    }
  },
  {
    id: 'status', header: 'Status', width: 100,
    cell: (item) => {
      if (item._isGroupHeader) return '';
      const s = STATUS_MAP[item.status] || { type: 'stopped', label: item.status };
      return <StatusIndicator type={s.type}>{s.label}</StatusIndicator>;
    }
  },
];

function buildGroupedRows(tools) {
  const groups = {};
  for (const t of tools) {
    const parent = t.parent || 'Other';
    if (!groups[parent]) groups[parent] = [];
    groups[parent].push(t);
  }

  const rows = [];
  for (const group of GROUP_ORDER) {
    let items = groups[group];
    if (!items || items.length === 0) continue;

    // Enforce sub-ordering within Listing Creation
    if (group === 'Listing Creation') {
      items = items.slice().sort((a, b) => {
        const ai = LISTING_CREATION_ORDER.indexOf(a.tool);
        const bi = LISTING_CREATION_ORDER.indexOf(b.tool);
        return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
      });
    }

    rows.push({ _isGroupHeader: true, _groupName: group, tool: group, id: `group-${group}` });
    for (const item of items) {
      rows.push({ ...item, id: `${group}-${item.tool}` });
    }
  }
  return rows;
}

export default function AdoptionTracker() {
  const [rawTools, setRawTools] = useState([]);
  const [dailyRuns, setDailyRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userFilter, setUserFilter] = useState('all');
  const [dateRange, setDateRange] = useState(null);

  const fetchStats = async (dateValue) => {
    setLoading(true);
    try {
      const { startDate, endDate } = resolveDateRange(dateValue);
      let url = `${API_BASE_URL}/api/adoption-stats`;
      const params = [];
      if (startDate) params.push(`start_date=${startDate}`);
      if (endDate) params.push(`end_date=${endDate}`);
      if (params.length) url += `?${params.join('&')}`;

      const res = await apiFetch(url);
      if (!res.ok) throw new Error(`Failed: ${res.status}`);
      const data = await res.json();
      setRawTools([...(data.tools || []), ...STATIC_TOOLS]);
      setDailyRuns(data.dailyRuns || []);
    } catch (err) {
      console.error('Failed to fetch adoption stats:', err);
      setRawTools(STATIC_TOOLS);
      setDailyRuns([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStats(dateRange); }, []);

  const rows = useMemo(() => {
    const filtered = rawTools.map((tool) => {
      const { users, runs, topUsers, itemsProcessed } = applyUserFilter(tool, userFilter);
      return {
        ...tool,
        _filteredUsers: users,
        _filteredRuns: runs,
        _filteredTopUsers: topUsers,
        itemsProcessed,
      };
    });
    return buildGroupedRows(filtered);
  }, [rawTools, userFilter]);

  const handleDateChange = ({ detail }) => {
    setDateRange(detail.value);
    fetchStats(detail.value);
  };

  return (
    <div className="adoption-tracker">
      <Container
        header={
          <Header
            variant="h2"
            description="Usage overview across all SmartBiz tools"
          >
            📊 Tool Adoption
          </Header>
        }
      >
        {loading ? (
          <Box textAlign="center" padding="l">
            <Spinner size="large" />
            <Box variant="p" color="text-body-secondary" margin={{ top: 's' }}>Loading usage data...</Box>
          </Box>
        ) : (
          <>
            <DailyRunsChart dailyRuns={dailyRuns} />
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <SegmentedControl
                selectedId={userFilter}
                onChange={({ detail }) => setUserFilter(detail.selectedId)}
                options={USER_FILTER_OPTIONS}
              />
              <DateRangePicker
                onChange={handleDateChange}
                value={dateRange}
                relativeOptions={DATE_RANGE_PRESETS}
                placeholder="All time"
                dateOnly
                rangeSelectorMode="default"
                i18nStrings={{
                  todayAriaLabel: "Today",
                  relativeModeTitle: "Relative",
                  absoluteModeTitle: "Absolute",
                  relativeRangeSelectionHeading: "Choose a range",
                  cancelButtonLabel: "Cancel",
                  applyButtonLabel: "Apply",
                  clearButtonLabel: "Clear",
                  startDateLabel: "Start date",
                  endDateLabel: "End date",
                  dateTimeConstraintText: "",
                  customRelativeRangeOptionLabel: "Custom range",
                  customRelativeRangeOptionDescription: "Set a custom range in days",
                  customRelativeRangeUnitLabel: "Unit",
                  customRelativeRangeDurationLabel: "Duration",
                  customRelativeRangeDurationPlaceholder: "Enter number",
                  formatRelativeRange: (value) => {
                    const unit = value.amount === 1 ? value.unit : `${value.unit}s`;
                    return `Last ${value.amount} ${unit}`;
                  },
                  formatUnit: (unit, value) => {
                    if (value === 1) return unit;
                    return `${unit}s`;
                  },
                }}
                customRelativeRangeUnits={['day', 'week', 'month']}
                isValidRange={(value) => {
                  if (!value) return { valid: true };
                  if (value.type === 'absolute') {
                    if (!value.startDate || !value.endDate) return { valid: false, errorMessage: 'Select both dates' };
                    if (new Date(value.startDate) > new Date(value.endDate)) return { valid: false, errorMessage: 'Start must be before end' };
                  }
                  return { valid: true };
                }}
              />
            </div>
            <Table
              columnDefinitions={COLUMN_DEFS}
              items={rows}
              variant="embedded"
              sortingDisabled
              empty={
                <Box textAlign="center" color="inherit" padding="l">
                  <SpaceBetween size="xxs">
                    <Box variant="p" color="inherit"><b>No data</b></Box>
                    <Box variant="p" color="text-body-secondary">Tool usage data will appear here.</Box>
                  </SpaceBetween>
                </Box>
              }
            />
          </>
        )}
      </Container>
    </div>
  );
}
